import React, { useState } from 'react';
import { useGetCalendarEventsQuery } from '@/APIConnect';

interface CalendarEvent {
  id: string;
  type: 'Sessions' | 'Deadlines' | 'Office Hours' | 'Study Groups' | 'AMAs' | 'Course';
  visualType: 'live' | 'deadline' | 'office' | 'peer' | 'ama' | 'course';
  time: string;
  duration?: string;
  title: string;
  date: string; // ISO yyyy-mm-dd
  meta?: {
    course?: string;
    instructor?: string;
    attendees?: string | number;
    details?: string;
  };
}

interface CalendarViewProps {
  activeFilters?: string[];
}

const CalendarView: React.FC<CalendarViewProps> = ({ activeFilters = ['all'] }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Calculate week range for API
  const getWeekRange = (date: Date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return {
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10),
    };
  };

  const { start, end } = getWeekRange(currentDate);
  const { data: apiData, isLoading } = useGetCalendarEventsQuery({ start, end });

  // Get current week days
  const getWeekDays = () => {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      weekDays.push(date);
    }
    return weekDays;
  };

  const weekDays = getWeekDays();

  // Mapping logic
  const mapApiData = (): CalendarEvent[] => {
    const data = apiData?.resultObject || apiData;
    if (!data) return [];

    const mappedEvents: CalendarEvent[] = [];

    // Map Events
    (data.events || []).forEach((ev: any) => {
      const startD = new Date(ev.startTime);
      const endD = new Date(ev.endTime);
      const diffMin = Math.round((endD.getTime() - startD.getTime()) / 60000);

      let type: CalendarEvent['type'] = 'Sessions';
      let visualType: CalendarEvent['visualType'] = 'live';

      const desc = (ev.description || '').toLowerCase();
      if (desc.includes('office')) {
        type = 'Office Hours'; visualType = 'office';
      } else if (desc.includes('peer') || desc.includes('study') || desc.includes('group')) {
        type = 'Study Groups'; visualType = 'peer';
      } else if (desc.includes('ama')) {
        type = 'AMAs'; visualType = 'ama';
      }

      mappedEvents.push({
        id: ev.id,
        type,
        visualType,
        time: startD.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        duration: diffMin >= 60 ? `${Math.round(diffMin / 60)} hr` : `${diffMin} min`,
        title: ev.description || 'Event',
        date: startD.toISOString().slice(0, 10),
        meta: {
          course: 'Module 1',
          instructor: 'Dr. Aarti Sharma'
        }
      });
    });

    // Map Deadlines
    (data.deadlines || []).forEach((dl: any) => {
      const due = new Date(dl.dueDate);
      mappedEvents.push({
        id: dl.referenceId,
        type: 'Deadlines',
        visualType: 'deadline',
        time: due.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        duration: 'due',
        title: dl.assignmentTitle,
        date: due.toISOString().slice(0, 10),
        meta: {
          details: dl.assignmentTitle.includes('reflection') ? '500 words' : ''
        }
      });
    });

    // Map Notes
    (data.notes || []).forEach((note: any) => {
      if (note.reminderDateTime) {
        const d = new Date(note.reminderDateTime);
        mappedEvents.push({
          id: note.id,
          type: 'Course',
          visualType: 'course',
          time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          duration: 'unlock',
          title: note.title,
          date: d.toISOString().slice(0, 10)
        });
      }
    });

    return mappedEvents;
  };

  const allEventsRaw = mapApiData();
  const allEvents = allEventsRaw.filter(e => {
    if (activeFilters.includes('all')) return true;
    const filterTypeMap: Record<string, string> = {
      'live': 'Live Lecture',
      'deadline': 'Assignment Deadline',
      'office': 'Office Hours',
      'peer': 'Study Group',
      'ama': 'AMA'
    };
    return activeFilters.some(filter => e.type === filter || (filterTypeMap[e.visualType] === filter));
  });

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction === 'prev' ? -7 : 7));
    setCurrentDate(newDate);
  };

  const formatWeekRange = () => {
    const startD = weekDays[0];
    const endD = weekDays[6];
    return `Week of ${startD.getDate()} — ${endD.getDate()} ${startD.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
  };

  if (isLoading) return <div className="day-empty-msg">Loading events...</div>;

  return (
    <>
      {/* Week navigator */}
      <div className="week-nav">
        <div className="week-nav-l">
          <div className="nav-arrows">
            <button className="nav-arrow" onClick={() => navigateWeek('prev')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6l6 6" /></svg>
            </button>
            <button className="nav-arrow" onClick={() => navigateWeek('next')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6l-6 6" /></svg>
            </button>
          </div>
          <div>
            <div className="week-range">{formatWeekRange()}</div>
            <div className="week-meta">{allEvents.length} events this week · {allEvents.filter(e => e.date === new Date().toISOString().slice(0, 10)).length} today</div>
          </div>
        </div>
        <button className="today-btn" onClick={() => setCurrentDate(new Date())}>Today</button>
      </div>

      {/* Two-column grid */}
      <div className="page-grid">
        <div className="col-main">
          {weekDays.map(day => {
            const dateStr = day.toISOString().slice(0, 10);
            const dayEvents = allEvents.filter(e => e.date === dateStr);
            const isToday = dateStr === new Date().toISOString().slice(0, 10);

            return (
              <div key={dateStr} className={`day-card ${dayEvents.length === 0 ? 'empty' : ''} ${isToday ? 'today' : ''}`}>
                <div className="day-head">
                  <div className="day-label">
                    <span className="day-num">{day.getDate()}</span>
                    <div className="day-info">
                      <span className="day-name">{day.toLocaleDateString('en-US', { weekday: 'long' })}</span>
                      <span className="day-month">{day.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                    </div>
                    {isToday && <span className="day-today-tag">Today</span>}
                  </div>
                  <span className="day-count">{dayEvents.length === 0 ? 'No events' : `${dayEvents.length} events`}</span>
                </div>

                {dayEvents.length === 0 ? (
                  <div className="day-empty-msg">A quiet day — good for catching up on reading.</div>
                ) : (
                  <div className="events">
                    {dayEvents.map(ev => (
                      <div key={ev.id} className={`ev ${ev.visualType}`}>
                        <div className="ev-rail"></div>
                        <div className="ev-time">
                          <span className="ev-time-h">{ev.time}</span>
                          <span className="ev-time-d">{ev.duration}</span>
                        </div>
                        <div className="ev-body">
                          <div className="ev-head">
                            <span className="ev-type">{ev.type}</span>
                          </div>
                          <div className="ev-title">{ev.title}</div>
                          <div className="ev-meta">
                            {ev.meta?.course && <span>{ev.meta.course}</span>}
                            {ev.meta?.instructor && <><span className="dot"></span><span>{ev.meta.instructor}</span></>}
                            {ev.meta?.attendees && <><span className="dot"></span><span>{ev.meta.attendees} attending</span></>}
                            {ev.meta?.details && <><span className="dot"></span><span>{ev.meta.details}</span></>}
                          </div>
                        </div>
                        <div className="ev-action">
                          {ev.visualType === 'live' && <button className="ev-btn primary">Join live</button>}
                          {ev.visualType === 'office' && <button className="ev-btn primary">Join</button>}
                          {ev.visualType === 'deadline' && <button className="ev-btn primary">Submit</button>}
                          {ev.visualType === 'peer' && <button className="ev-btn">RSVP</button>}
                          <button className="ev-btn">View</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <aside className="col-side">
          {/* Up Next Card */}
          <div className="upnext">
            <div className="upnext-lbl">Up next today</div>
            <div className="upnext-time">Wed 20 May · 5:00 PM</div>
            <div className="upnext-title">Why Global Health Matters — live discussion</div>
            <div className="upnext-countdown">
              <div className="uc"><span className="uc-val">0</span><span className="uc-lbl">Days</span></div>
              <div className="uc"><span className="uc-val">0</span><span className="uc-lbl">Hours</span></div>
              <div className="uc"><span className="uc-val">33</span><span className="uc-lbl">Min</span></div>
            </div>
            <button className="upnext-cta">
              Join live session <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>

          {/* This Week Summary */}
          <div className="panel">
            <div className="panel-title">This Week</div>
            <div className="sum-grid">
              <div className="sum-stat">
                <div className="sum-val teal">2</div>
                <div className="sum-lbl">Live Sessions</div>
              </div>
              <div className="sum-stat">
                <div className="sum-val violet">2</div>
                <div className="sum-lbl">Office Hours</div>
              </div>
              <div className="sum-stat">
                <div className="sum-val rose">2</div>
                <div className="sum-lbl">Deadlines</div>
              </div>
              <div className="sum-stat">
                <div className="sum-val gold">1</div>
                <div className="sum-lbl">Expert AMA</div>
              </div>
            </div>
          </div>

          {/* My Calendars filters */}
          <div className="panel">
            <div className="panel-title">My Calendars</div>
            <div className="cal-list">
              <div className="cal-row c-teal">
                <span className="cal-cb on"><svg fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg></span>
                <span className="cal-name">Health Systems & Innovation</span>
                <span className="cal-count">6</span>
              </div>
              <div className="cal-row c-peach">
                <span className="cal-cb on"><svg fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg></span>
                <span className="cal-name">Innovation Pod 2026</span>
                <span className="cal-count">2</span>
              </div>
              <div className="cal-row c-sky">
                <span className="cal-cb on"><svg fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg></span>
                <span className="cal-name">Peer study groups</span>
                <span className="cal-count">2</span>
              </div>
              <div className="cal-row c-violet">
                <span className="cal-cb on"><svg fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg></span>
                <span className="cal-name">Personal events</span>
                <span className="cal-count">0</span>
              </div>
            </div>
            <button className="quick-add">
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg> Add an event
            </button>
          </div>
        </aside>
      </div>
    </>
  );
};

export default CalendarView;
